import type { RumInitConfiguration } from '@datadog/browser-rum';
import { IAppConfig } from '../models/app-config';
import { DatadogRumEnvTag } from './datadog-rum.types';

/** Service name registered against the RUM Application; must match backend `DD_SERVICE`. */
export const DATADOG_RUM_SERVICE_NAME = 'rum-portfolio-management-frontend';

/** Datadog site (region). Equifax corporate account → US1. */
export const DATADOG_SITE = 'datadoghq.com';

/** Hostnames where RUM must never initialize (avoid panel noise and wasted quota). */
const NEVER_INITIALIZE_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

/** Maps the uppercase env name to the lowercase Datadog `env` tag; unknown ⇒ `local`. */
export function mapEnvironmentToDatadogTag(environmentName: string): DatadogRumEnvTag {
  const normalized = (environmentName ?? '').toLowerCase();
  switch (normalized) {
    case 'dev':
    case 'hml':
    case 'uat':
    case 'prod':
      return normalized;
    default:
      return 'local';
  }
}

/**
 * Returns `true` only when Datadog is enabled with non-empty `applicationId`/`clientToken`
 * AND the current hostname is not a localhost variant.
 */
export function shouldInitializeDatadogRum(
  authConfig: IAppConfig | null | undefined,
  hostname: string,
): boolean {
  const datadog = authConfig?.datadog;
  if (!datadog?.enabled) return false;
  if (!datadog.applicationId?.trim() || !datadog.clientToken?.trim()) return false;
  if (NEVER_INITIALIZE_HOSTNAMES.has(hostname)) return false;
  return true;
}

/**
 * Builds `allowedTracingUrls` matchers from the microservice base URLs.
 * Uses W3C `tracecontext` propagation to match backend `DD_TRACE_PROPAGATION_STYLE_EXTRACT`.
 * Also covers the same-origin `/backend/` path produced by the NGINX proxy_pass.
 */
export function buildAllowedTracingUrls(
  authConfig: IAppConfig,
): RumInitConfiguration['allowedTracingUrls'] {
  const microserviceBaseUrls = [
    authConfig.alertServiceBaseUrl,
    authConfig.billingServiceBaseUrl,
    authConfig.bulkLoadServiceBaseUrl,
    authConfig.configurationServiceBaseUrl,
    authConfig.dataIngestionServiceBaseUrl,
    authConfig.walletServiceBaseUrl,
    authConfig.backendUri,
  ].filter((url): url is string => typeof url === 'string' && url.trim().length > 0);

  const uniqueBaseUrls = Array.from(
    new Set(microserviceBaseUrls.map((url) => url.replace(/\/$/, ''))),
  );

  const exactMatchers = uniqueBaseUrls.map((baseUrl) => ({
    match: (url: string) => url.startsWith(baseUrl),
    propagatorTypes: ['tracecontext' as const],
  }));

  const sameOriginProxyMatcher = {
    match: (url: string) => url.includes('/backend/'),
    propagatorTypes: ['tracecontext' as const],
  };

  return [...exactMatchers, sameOriginProxyMatcher];
}

export interface BuildRumInitConfigInputs {
  authConfig: IAppConfig;
  environmentName: string;
  /** PENDING: should become `${pkg.version}+${GIT_COMMIT.slice(0,7)}` once Jenkins is wired. */
  version: string;
}

/** Pure builder so the bootstrap stays trivial and the config can be unit-tested. */
export function buildRumInitConfig(inputs: BuildRumInitConfigInputs): RumInitConfiguration {
  const { authConfig, environmentName, version } = inputs;
  const datadog = authConfig.datadog;

  if (!datadog) {
    throw new Error(
      'buildRumInitConfig: authConfig.datadog is missing — guard with shouldInitializeDatadogRum first.',
    );
  }

  return {
    applicationId: datadog.applicationId,
    clientToken: datadog.clientToken,
    site: DATADOG_SITE,
    service: DATADOG_RUM_SERVICE_NAME,
    env: mapEnvironmentToDatadogTag(environmentName),
    version,
    sessionSampleRate: datadog.sessionSampleRate,
    sessionReplaySampleRate: datadog.sessionReplaySampleRate,
    // LGPD: mask everything in Session Replay; opt-in per-element via `data-dd-privacy="allow"`.
    defaultPrivacyLevel: 'mask',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: buildAllowedTracingUrls(authConfig),
  };
}
